<script setup>
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from './ModalBox.vue'
import StreamService from '@/services/StreamService'
import PaywallService from '@/services/PaywallService'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'

const props = defineProps({
	modalId: {
		type: String,
		default: 'modal-confirm',
	},
	modalSize: {
		type: String,
		default: 'sm',
		validator(v) {
			return ['xl', 'lg', 'md', 'sm'].some((size) => size === v)
		},
	},
	streamId: {
		type: String,
		required: true,
	},
	streamName: {
		type: String,
		required: true,
	},
	modalType: {
		type: String,
		default: 'primary',
	},
	message: {
		type: String,
		required: false,
	},
	okText: {
		type: String,
		default: 'Yes',
	},
	cancelText: {
		type: String,
		default: 'No',
	},
	showConfirm: {
		type: Boolean,
		default: true,
	},
	closeoutsideClick: {
		type: Boolean,
		default: false,
	},
	confirmCancelEvent: {
		type: Boolean,
		default: true,
	},
})

const emit = defineEmits(['modal-confirm', 'modal-cancel', 'modal-dismiss'])

const store = useStore()
const { notify } = useNotification()

const id = ref('')
const newName = ref('')

const confirmButtonType = () => {
	const map = { primary: 'main', danger: 'red', secondary: 'secondary' }
	return map[props.modalType] ?? 'main'
}

const syncFromProps = () => {
	id.value = props.streamId
	newName.value = props.streamName
}

const { isVisible, hide } = useLegacyModalBridge(() => props.modalId, {
	onShow: syncFromProps,
	onHidden: () => {
		newName.value = ''
		id.value = ''
	},
})

watch(() => [props.streamId, props.streamName], syncFromProps)

const dismiss = (cancel = true) => {
	if (cancel) {
		emit('modal-cancel')
	}
	hide()
	emit('modal-dismiss')
}

const onConfirm = async () => {
	if (!newName.value) newName.value = props.streamName
	if (props.streamName === newName.value) return

	try {
		await StreamService.setStreamName(id.value, newName.value)
		PaywallService.updateStreamPaywall(id.value, { title: newName.value }).catch((err) => {
			console.error('update paywall stream name failed: ', err)
		})
		notify({
			group: 'success',
			title: 'Stream name was changed successfully',
		})
		window.trackEvent?.('Stream Renamed', {
			streamId: id.value,
			oldName: props.streamName,
			newName: newName.value,
		})
		hide()
		emit('modal-dismiss')
		emit('modal-confirm')
		// Force-refresh streams so the new folder name shows immediately.
		store.dispatch('Streams/getStreams', true).catch((err) => {
			console.error('getStreams refresh failed after rename:', err)
		})
	} catch (err) {
		newName.value = props.streamName
		notify({
			group: 'error',
			title: "Couldn't rename the folder.",
			text: err.message,
		})
	}
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		:close-on-backdrop="!closeoutsideClick"
		@close="dismiss(true)"
	>
		<div class="px-6 pt-6">
			<h3 v-if="message" class="text-s-m text-500 mb-4">{{ message }}</h3>
			<label class="text-s-s text-dark-8" for="newName">Folder name</label>
			<FormInput
				id="newName"
				v-model="newName"
				size="md"
				placeholder="name"
				class="mb-3 mt-1"
			/>
			<slot />
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6 border-t border-surface-3">
				<FormButton type="secondary" size="md" @click="dismiss(true)">
					{{ cancelText }}
				</FormButton>
				<FormButton v-if="showConfirm" :type="confirmButtonType()" size="md" :disabled="!newName.trim()" @click="onConfirm">
					{{ okText }}
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
